import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { useCart } from '@/lib/cart-context';
import { buildGuestOrderTrackPath, readGuestCheckoutContext } from '@/lib/guest-order-track';
import { readPendingCheckout, savePendingCheckout, clearPendingCheckout } from '@/lib/pending-checkout';
import { orders } from '@/lib/api';
import { CheckCircle, XCircle, Loader2, Home, ShoppingBag, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const ZiraatCallbackPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { clearCart, restoreCartItems } = useCart();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const isSuccess = location.pathname.includes('/success');
  const orderId = searchParams.get('order_id');
  const errorMessageRaw = searchParams.get('error');
  // Banka ISO-8859 mesajı URL'de bozulunca "ba�lan�lam�yor" görünür — bilinen host hatasını düzelt
  const errorMessage = (() => {
    const raw = errorMessageRaw || '';
    if (!raw) return raw;
    const low = raw.toLowerCase();
    if (
      (low.includes('host') && (low.includes('network') || low.includes('baglan') || low.includes('bağlan') || raw.includes('�')))
      || (low.includes('network') && low.includes('problem'))
    ) {
      return (
        "Host'a bağlanılamıyor (banka ağ/host hatası). " +
        'Bu genelde Ziraat/Payten tarafında geçici bir sorundur — birkaç dakika sonra tekrar deneyin. ' +
        'Devam ederse Ziraat üye işyeri / sanal POS desteğini arayın.'
      );
    }
    return raw;
  })();

  const rebuildCartForRetry = useCallback(async () => {
    let pending = readPendingCheckout();

    // Yedek yoksa veya sepet kalemi yoksa siparişten çek
    if ((!pending?.items?.length) && orderId) {
      try {
        const guestEmail =
          pending?.guestInfo?.email
          || readGuestCheckoutContext()?.email
          || '';
        const res = await orders.checkoutRetry(orderId, user ? undefined : guestEmail);
        const data = res.data || {};
        if (data.items?.length) {
          pending = {
            orderId: data.order_id || orderId,
            orderNumber: data.order_number,
            total: data.total,
            currency: data.currency,
            paymentPending: true,
            items: data.items,
            address: data.shipping_address || {},
            guestInfo: data.guest_email
              ? { email: data.guest_email, phone: '' }
              : (pending?.guestInfo || null),
          };
          savePendingCheckout(pending);
        }
      } catch (e) {
        console.warn('checkout-retry fetch failed', e);
      }
    }

    if (pending?.items?.length) {
      await restoreCartItems(pending.items);
      return pending;
    }
    return null;
  }, [orderId, user, restoreCartItems]);

  // Callback sonucu yalnızca bir kez işle — rebuildCartForRetry değişince sonsuz döngü olmasın
  const processedCallbackRef = useRef(false);
  useEffect(() => {
    if (processedCallbackRef.current) return;
    processedCallbackRef.current = true;

    const processCallback = async () => {
      if (isSuccess && orderId) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/ziraat/status/${orderId}`
          );
          const data = await response.json();

          if (data.status === 'completed') {
            setStatus('success');
            setPaymentInfo(data);
            clearPendingCheckout();
            try {
              await clearCart();
            } catch {
              /* ignore */
            }
          } else {
            setStatus('failed');
            setError(data.error || t('checkout.paymentIncomplete'));
            // Başarısız: sepeti hemen geri yükle
            await rebuildCartForRetry();
          }
        } catch (err) {
          // Ağ hatasında başarı varsayma — sepeti silme
          setStatus('failed');
          setError(t('checkout.paymentIncomplete'));
          await rebuildCartForRetry();
        }
      } else {
        setStatus('failed');
        setError(errorMessage || t('checkout.secure3dFailed'));
        await rebuildCartForRetry();
      }
    };

    processCallback();
  }, [isSuccess, orderId, errorMessage, t, clearCart, rebuildCartForRetry]);

  const handleTryAgain = async () => {
    setRetrying(true);
    try {
      const pending = await rebuildCartForRetry();
      if (!pending?.items?.length) {
        toast.error(
          t(
            'checkout.cartRestoreFailed',
            'Sepet geri yüklenemedi. Lütfen ürünleri tekrar sepete ekleyin.'
          )
        );
      }
      const lang = (i18n.language || 'tr').split('-')[0];
      navigate(`/${lang}/checkout`, { state: { fromPaymentRetry: true } });
    } catch (e) {
      console.error('Retry restore failed', e);
      navigate('/checkout', { state: { fromPaymentRetry: true } });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-lg mx-auto px-4">
        <Card className="shadow-xl">
          <CardHeader className="text-center pb-2">
            {status === 'loading' && (
              <div className="flex flex-col items-center">
                <Loader2 className="w-16 h-16 text-green-600 animate-spin mb-4" />
                <CardTitle className="text-xl">{t('checkout.processingPayment')}</CardTitle>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <CardTitle className="text-2xl text-green-700">
                  {t('checkout.paymentSuccess')}
                </CardTitle>
              </div>
            )}

            {status === 'failed' && (
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
                <CardTitle className="text-2xl text-red-700">
                  {t('checkout.paymentFailed')}
                </CardTitle>
              </div>
            )}
          </CardHeader>

          <CardContent className="text-center space-y-6">
            {status === 'loading' && (
              <p className="text-gray-600">
                {t('checkout.ziraat3dVerifyMessage')}
              </p>
            )}

            {status === 'success' && (
              <>
                <div className="bg-green-50 rounded-lg p-4 text-left">
                  <h4 className="font-semibold text-green-800 mb-2">{t('checkout.orderDetails')}</h4>
                  <div className="space-y-1 text-sm text-green-700">
                    <p><strong>{t('checkout.orderNumber')}:</strong> {paymentInfo?.order_id}</p>
                    {paymentInfo?.amount && (
                      <p><strong>{t('checkout.paidAmount')}:</strong> {paymentInfo.amount.toFixed(2)} TL</p>
                    )}
                    {paymentInfo?.installment > 1 && (
                      <p><strong>{t('checkout.installment')}:</strong> {t('checkout.installmentCount', { count: paymentInfo.installment })}</p>
                    )}
                    <p><strong>{t('checkout.paymentMethod')}:</strong> {t('checkout.paymentMethodZiraat')}</p>
                  </div>
                </div>

                <p className="text-gray-600">
                  {t('checkout.orderCreatedMessage')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                  <Button
                    onClick={() => {
                      if (user) {
                        navigate(`/orders/${orderId}`);
                        return;
                      }
                      const ctx = readGuestCheckoutContext();
                      navigate(buildGuestOrderTrackPath({
                        orderNumber: ctx.orderNumber || orderId,
                        email: ctx.email,
                        lang: i18n.language,
                      }));
                    }}
                    className="flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    {t('checkout.viewOrder')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    {t('checkout.backToHome')}
                  </Button>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="bg-red-50 rounded-lg p-4 text-left">
                  <h4 className="font-semibold text-red-800 mb-2">{t('checkout.errorDetails')}</h4>
                  <p className="text-sm text-red-700">{error}</p>
                </div>

                <p className="text-gray-600">
                  {t('checkout.paymentFailedMessage')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                  <Button
                    onClick={handleTryAgain}
                    disabled={retrying}
                    className="flex items-center gap-2"
                  >
                    {retrying ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    {t('checkout.tryAgain')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      await rebuildCartForRetry();
                      navigate('/cart');
                    }}
                    className="flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    {t('checkout.backToCart')}
                  </Button>
                </div>
              </>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>{t('checkout.ziraatSecurePayment')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ZiraatCallbackPage;
